import React, { useState, useEffect } from 'react';
import { format, startOfWeek, addDays, isSameDay, parseISO, startOfDay, endOfDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/services/api';
import { toast } from 'sonner';
import BookingModal from '@/components/BookingModal';
import BookingDetailModal from '@/components/BookingDetailModal';

const Bookings = ({ user }) => {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewMode, setViewMode] = useState('general'); // 'general' or 'by-room'
  const [selectedRoom, setSelectedRoom] = useState(null);

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const timeSlots = Array.from({ length: 13 }, (_, i) => i + 8); // 8 AM - 8 PM

  useEffect(() => {
    loadData();
  }, [currentWeek, selectedLocation]);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const filtered = bookings.filter((booking) => {
        const clientName = booking.client_name || clients.find((c) => c.id === booking.client_id)?.name || '';
        const serviceName = booking.service_name || services.find((s) => s.id === booking.service_id)?.name || '';
        return (
          clientName.toLowerCase().includes(term) ||
          serviceName.toLowerCase().includes(term)
        );
      });
      setFilteredBookings(filtered);
    } else {
      setFilteredBookings(bookings);
    }
  }, [searchTerm, bookings, clients, services]);

  const loadData = async () => {
    try {
      setLoading(true);
      const startDate = startOfDay(weekStart).toISOString();
      const endDate = endOfDay(addDays(weekStart, 6)).toISOString();

      const [bookingsRes, servicesRes, staffRes, clientsRes, roomsRes, locationsRes] =
        await Promise.all([
          api.getBookings(startDate, endDate, selectedLocation),
          api.getServices(),
          api.getStaff(),
          api.getClients(),
          api.getRooms(),
          api.getLocations(),
        ]);

      setBookings(bookingsRes.data);
      setServices(servicesRes.data);
      setStaff(staffRes.data);
      setClients(clientsRes.data);
      setRooms(roomsRes.data);
      setLocations(locationsRes.data);

      if (!selectedLocation && locationsRes.data.length > 0) {
        setSelectedLocation(locationsRes.data[0].id);
      }
      
      // Auto-seleccionar la primera sala si no hay ninguna seleccionada
      if (!selectedRoom && roomsRes.data.length > 0) {
        setSelectedRoom(roomsRes.data[0].id);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevWeek = () => {
    setCurrentWeek(addDays(currentWeek, -7));
  };

  const handleNextWeek = () => {
    setCurrentWeek(addDays(currentWeek, 7));
  };

  const handleToday = () => {
    setCurrentWeek(new Date());
  };

  const getBookingsForSlot = (day, hour) => {
    return filteredBookings.filter((booking) => {
      const bookingDate = parseISO(booking.booking_datetime);
      return (
        isSameDay(bookingDate, day) &&
        bookingDate.getHours() === hour
      );
    });
  };
  
  // Función para calcular lanes (carriles) de reservas simultáneas en una sala
  const calculateBookingLanes = (dayBookings, roomCapacity) => {
    if (!dayBookings || dayBookings.length === 0) return [];
    
    // Ordenar por hora de inicio
    const sorted = [...dayBookings].sort((a, b) => {
      return parseISO(a.booking_datetime).getTime() - parseISO(b.booking_datetime).getTime();
    });
    
    const lanes = [];
    
    sorted.forEach((booking) => {
      const bookingStart = parseISO(booking.booking_datetime);
      const service = services.find(s => s.id === booking.service_id);
      const duration = service?.duration_minutes || 60;
      const bookingEnd = new Date(bookingStart.getTime() + duration * 60000);
      
      // Encontrar un lane disponible
      let assignedLane = -1;
      for (let i = 0; i < roomCapacity; i++) {
        const laneBookings = lanes[i] || [];
        // Verificar si hay conflicto con alguna reserva en este lane
        const hasConflict = laneBookings.some(existingBooking => {
          const existingStart = parseISO(existingBooking.booking_datetime);
          const existingService = services.find(s => s.id === existingBooking.service_id);
          const existingDuration = existingService?.duration_minutes || 60;
          const existingEnd = new Date(existingStart.getTime() + existingDuration * 60000);
          
          // Hay conflicto si los rangos se solapan
          return bookingStart < existingEnd && bookingEnd > existingStart;
        });
        
        if (!hasConflict) {
          assignedLane = i;
          break;
        }
      }
      
      // Si no encontramos lane disponible, usar el primero (se superpondrán)
      if (assignedLane === -1) assignedLane = 0;
      
      if (!lanes[assignedLane]) lanes[assignedLane] = [];
      lanes[assignedLane].push({ ...booking, lane: assignedLane });
    });
    
    // Aplanar el array con la info del lane
    return lanes.flat().map(b => b);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'reserved':
        return 'bg-yellow-400 border-yellow-400'; // Reservado - sin seña
      case 'cancelled':
        return 'bg-red-500 border-red-500'; // Cancelado
      case 'rescheduled':
        return 'bg-purple-500 border-purple-500'; // Reagendado
      case 'confirmed_deposit':
        return 'bg-orange-500 border-orange-500'; // Confirmado Seña - tiene saldo
      case 'confirmed_full':
        return 'bg-[#6B7C59] border-[#6B7C59]'; // Confirmado Completo - pagó 100%
      case 'confirmed_manual':
        return 'bg-blue-500 border-blue-500'; // Confirmado Manual - cliente habitual
      case 'completed_unpaid':
        return 'bg-gray-400 border-gray-400'; // Finalizado No Pago
      case 'completed_paid':
        return 'bg-[#4a5c3a] border-[#4a5c3a]'; // Finalizado Pagado - verde oscuro
      default:
        return 'bg-gray-300 border-gray-300';
    }
  };

  const handleBookingClick = async (booking) => {
    setSelectedBooking(booking);
    setShowDetailModal(true);
  };

  const handleBookingCreated = () => {
    setShowCreateModal(false);
    loadData();
    toast.success('Reserva creada exitosamente');
  };

  const handleBookingUpdated = () => {
    setShowDetailModal(false);
    loadData();
    toast.success('Reserva actualizada');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-[#C9A961] text-xl font-cormorant">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-6" data-testid="bookings-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-cormorant font-bold text-[#2C2C2C]">
              Calendario de Reservas
            </h1>
            <p className="text-[#6B6B6B] mt-1">Gestiona y visualiza todas las reservas</p>
          </div>

          <Button
            onClick={() => setShowCreateModal(true)}
            data-testid="create-booking-button"
            className="bg-[#C9A961] hover:bg-[#B89551] text-white"
          >
            <Plus className="mr-2" size={20} />
            Nueva Reserva
          </Button>
        </div>

        {/* Filters & Navigation */}
        <div className="mt-6 flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6B6B6B]" size={20} />
              <Input
                placeholder="Buscar por cliente o servicio..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-bookings-input"
                className="pl-10 border-[#C9A961]/30"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              onClick={handlePrevWeek}
              variant="outline"
              data-testid="prev-week-button"
              className="border-[#C9A961]/30 hover:border-[#C9A961]"
            >
              <ChevronLeft size={20} />
            </Button>
            <Button
              onClick={handleToday}
              variant="outline"
              data-testid="today-button"
              className="border-[#C9A961]/30 hover:border-[#C9A961]"
            >
              Hoy
            </Button>
            <Button
              onClick={handleNextWeek}
              variant="outline"
              data-testid="next-week-button"
              className="border-[#C9A961]/30 hover:border-[#C9A961]"
            >
              <ChevronRight size={20} />
            </Button>
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <h2 className="text-xl font-cormorant font-semibold text-[#C9A961]">
            {format(weekStart, 'd MMMM', { locale: es })} - {format(addDays(weekStart, 6), 'd MMMM yyyy', { locale: es })}
          </h2>
          
          {/* View Mode Selector */}
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setViewMode('general')}
              variant={viewMode === 'general' ? 'default' : 'outline'}
              className={viewMode === 'general' ? 'bg-[#C9A961] hover:bg-[#B89551]' : 'border-[#C9A961]/30'}
              size="sm"
            >
              Vista General
            </Button>
            <Button
              onClick={() => setViewMode('by-room')}
              variant={viewMode === 'by-room' ? 'default' : 'outline'}
              className={viewMode === 'by-room' ? 'bg-[#C9A961] hover:bg-[#B89551]' : 'border-[#C9A961]/30'}
              size="sm"
            >
              Por Sala
            </Button>
          </div>
        </div>
        
        {/* Room Selector (only visible in by-room mode) */}
        {viewMode === 'by-room' && rooms.length > 0 && (
          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm font-medium text-[#6B6B6B]">Sala:</span>
            <div className="flex gap-2 flex-wrap">
              {rooms.map((room) => (
                <Button
                  key={room.id}
                  onClick={() => setSelectedRoom(room.id)}
                  variant={selectedRoom === room.id ? 'default' : 'outline'}
                  className={selectedRoom === room.id ? 'bg-[#C9A961] hover:bg-[#B89551]' : 'border-[#C9A961]/30'}
                  size="sm"
                >
                  {room.name}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Calendar Grid */}
      {viewMode === 'general' ? (
        /* VISTA GENERAL - Compacta con Iniciales */
        <Card className="border-[#C9A961]/20 overflow-hidden" style={{ height: 'calc(100vh - 380px)', minHeight: '500px' }}>
          <div className="h-full flex flex-col">
            <div className="min-w-[1200px] h-full flex flex-col">
              {/* Header Row */}
              <div className="grid grid-cols-8 border-b-2 border-[#C9A961]/30 bg-[#F5F2ED] flex-shrink-0">
                <div className="p-2 text-xs font-semibold text-[#6B6B6B]">Hora</div>
                {weekDays.map((day) => (
                  <div
                    key={day.toISOString()}
                    className={`p-2 text-center ${ 
                      isSameDay(day, new Date()) ? 'bg-[#C9A961]/10' : ''
                    }`}
                  >
                    <div className="text-[10px] font-medium text-[#6B6B6B] uppercase">
                      {format(day, 'EEE', { locale: es })}
                    </div>
                    <div
                      className={`text-base font-cormorant font-semibold ${
                        isSameDay(day, new Date()) ? 'text-[#C9A961]' : 'text-[#2C2C2C]'
                      }`}
                    >
                      {format(day, 'd')}
                    </div>
                  </div>
                ))}
              </div>

              {/* Time Slots */}
              <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                {timeSlots.map((hour) => (
                  <div key={hour} className="grid grid-cols-8 border-b border-[#C9A961]/10" style={{ height: '50px' }}>
                    <div className="p-2 text-xs font-medium text-[#6B6B6B] border-r border-[#C9A961]/10 flex items-center">
                      {hour.toString().padStart(2, '0')}:00
                    </div>
                    {weekDays.map((day) => {
                      const slotBookings = getBookingsForSlot(day, hour);
                      return (
                        <div
                          key={`${day.toISOString()}-${hour}`}
                          className={`border-r border-[#C9A961]/10 p-1 flex items-center gap-1 flex-wrap ${
                            isSameDay(day, new Date()) ? 'bg-[#C9A961]/5' : ''
                          }`}
                        >
                          {slotBookings.length > 0 && (
                            <>
                              {slotBookings.slice(0, 3).map((booking) => {
                                // Usar nombres directos del booking si existen, sino buscar
                                const clientName = booking.client_name || clients.find((c) => c.id === booking.client_id)?.name || 'N/A';
                                const serviceName = booking.service_name || services.find((s) => s.id === booking.service_id)?.name || 'N/A';
                                const staffName = booking.staff_name || staff.find((s) => s.id === booking.staff_id)?.name || 'N/A';
                                const roomName = booking.room_name || rooms.find((r) => r.id === booking.room_id)?.name || 'N/A';
                                
                                const initials = clientName !== 'N/A' 
                                  ? clientName
                                      .split(' ')
                                      .map(n => n[0])
                                      .join('')
                                      .substring(0, 2)
                                      .toUpperCase() 
                                  : '?';
                                
                                return (
                                  <div
                                    key={booking.id}
                                    onClick={() => handleBookingClick(booking)}
                                    data-testid={`booking-card-${booking.id}`}
                                    className={`${getStatusColor(
                                      booking.status
                                    )} text-white px-2 py-1 rounded text-[10px] font-bold cursor-pointer hover:shadow-md transition-shadow relative group flex-shrink-0`}
                                    style={{ minWidth: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  >
                                    {initials}
                                    
                                    {/* Tooltip */}
                                    <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-[100] w-56 bg-white border-2 border-[#C9A961] rounded-lg shadow-2xl p-3 text-[#2C2C2C] pointer-events-none">
                                      <div className="space-y-1.5">
                                        <div>
                                          <div className="text-[10px] font-bold text-[#C9A961] uppercase">Cliente</div>
                                          <div className="text-sm font-semibold">{clientName}</div>
                                        </div>
                                        <div>
                                          <div className="text-[10px] font-bold text-[#C9A961] uppercase">Servicio</div>
                                          <div className="text-xs">{serviceName}</div>
                                        </div>
                                        <div>
                                          <div className="text-[10px] font-bold text-[#C9A961] uppercase">Sala</div>
                                          <div className="text-xs">{roomName}</div>
                                        </div>
                                        <div>
                                          <div className="text-[10px] font-bold text-[#C9A961] uppercase">Personal</div>
                                          <div className="text-xs">{staffName}</div>
                                        </div>
                                        <div className="pt-1 border-t border-[#C9A961]/20">
                                          <span className={`text-[10px] font-semibold ${
                                            booking.payment_status === 'paid' ? 'text-[#6B7C59]' : 
                                            booking.payment_status === 'partial' ? 'text-[#C9A961]' : 'text-red-500'
                                          }`}>
                                            {booking.payment_status === 'paid' ? '✓ Pagado' : 
                                             booking.payment_status === 'partial' ? '$ Seña' : '⚠ Sin pago'}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              {slotBookings.length > 3 && (
                                <div className="text-[10px] font-bold text-[#C9A961] px-1">
                                  +{slotBookings.length - 3}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      ) : (
        /* VISTA POR SALA - Con Duración Visual */
        <Card className="border-[#C9A961]/20 overflow-hidden" style={{ height: 'calc(100vh - 430px)', minHeight: '500px' }}>
          <div className="h-full flex flex-col">
            <div className="min-w-[1200px] h-full flex flex-col">
              {/* Header Row */}
              <div className="grid grid-cols-8 border-b-2 border-[#C9A961]/30 bg-[#F5F2ED] flex-shrink-0">
                <div className="p-2 text-xs font-semibold text-[#6B6B6B]">Hora</div>
                {weekDays.map((day) => (
                  <div
                    key={day.toISOString()}
                    className={`p-2 text-center ${ 
                      isSameDay(day, new Date()) ? 'bg-[#C9A961]/10' : ''
                    }`}
                  >
                    <div className="text-[10px] font-medium text-[#6B6B6B] uppercase">
                      {format(day, 'EEE', { locale: es })}
                    </div>
                    <div
                      className={`text-base font-cormorant font-semibold ${
                        isSameDay(day, new Date()) ? 'text-[#C9A961]' : 'text-[#2C2C2C]'
                      }`}
                    >
                      {format(day, 'd')}
                    </div>
                  </div>
                ))}
              </div>

                {/* Time Slots with Absolute Positioned Bookings */}
                <div className="flex-1 overflow-y-auto relative" style={{ scrollbarWidth: 'thin' }}>
                  {/* Hour Grid Lines */}
                  {timeSlots.map((hour) => (
                    <div key={hour} className="grid grid-cols-8 border-b border-[#C9A961]/10" style={{ height: '60px' }}>
                      <div className="p-2 text-xs font-medium text-[#6B6B6B] border-r border-[#C9A961]/10 flex items-start">
                        {hour.toString().padStart(2, '0')}:00
                      </div>
                      {weekDays.map((day) => (
                        <div
                          key={`${day.toISOString()}-${hour}`}
                          className={`border-r border-[#C9A961]/10 ${
                            isSameDay(day, new Date()) ? 'bg-[#C9A961]/5' : ''
                          }`}
                        />
                      ))}
                    </div>
                  ))}
                  
                  {/* Absolute Positioned Bookings for Selected Room */}
                  {weekDays.map((day, dayIndex) => {
                    const dayBookings = filteredBookings.filter((booking) => {
                      const bookingDate = parseISO(booking.booking_datetime);
                      return isSameDay(bookingDate, day) && booking.room_id === selectedRoom;
                    });
                    
                    const selectedRoomData = rooms.find(r => r.id === selectedRoom);
                    const roomCapacity = selectedRoomData?.capacity || 1;
                    
                    // Calcular lanes para distribuir reservas simultáneas
                    const bookingsWithLanes = calculateBookingLanes(dayBookings, roomCapacity);
                    
                    return (
                      <div key={day.toISOString()}>
                        {bookingsWithLanes.map((booking) => {
                          const bookingDate = parseISO(booking.booking_datetime);
                          const hour = bookingDate.getHours();
                          const minute = bookingDate.getMinutes();
                          
                          const client = clients.find((c) => c.id === booking.client_id);
                          const service = services.find((s) => s.id === booking.service_id);
                          const staffMember = staff.find((s) => s.id === booking.staff_id);
                          const room = rooms.find((r) => r.id === booking.room_id);
                          
                          const startHour = 8;
                          const slotHeightPx = 60;
                          
                          const minutesFromStart = (hour - startHour) * 60 + minute;
                          const topPosition = (minutesFromStart / 60) * slotHeightPx;
                          
                          const durationMinutes = service?.duration_minutes || 60;
                          const heightPx = (durationMinutes / 60) * slotHeightPx;
                          
                          const clientName = booking.client_name || client?.name || 'N/A';
                          const serviceName = booking.service_name || service?.name || 'N/A';
                          
                          const initials = clientName !== 'N/A'
                            ? clientName
                                .split(' ')
                                .map(n => n[0])
                                .join('')
                                .substring(0, 2)
                                .toUpperCase()
                            : '?';
                          
                          // Calcular posición considerando el lane
                          const columnWidth = 100 / 8;
                          const baseLeftPosition = columnWidth * (dayIndex + 1);
                          
                          // Dividir el ancho de la columna según la capacidad de la sala
                          const laneWidth = columnWidth / roomCapacity;
                          const leftPosition = baseLeftPosition + (booking.lane * laneWidth);
                          
                          return (
                            <div
                              key={booking.id}
                              onClick={() => handleBookingClick(booking)}
                              data-testid={`booking-card-${booking.id}`}
                              className={`${getStatusColor(
                                booking.status
                              )} text-white rounded cursor-pointer hover:shadow-lg transition-all group absolute overflow-hidden`}
                              style={{
                                top: `${topPosition}px`,
                                left: `${leftPosition}%`,
                                width: `${laneWidth - 0.5}%`,
                                height: `${heightPx - 2}px`,
                                minHeight: '40px',
                                zIndex: 10
                              }}
                            >
                              <div className="p-2 h-full flex flex-col justify-between">
                                <div>
                                  <div className="text-xs font-bold leading-tight">{initials}</div>
                                  <div className="text-[10px] leading-tight mt-0.5 opacity-90 truncate">
                                    {serviceName}
                                  </div>
                                </div>
                                {heightPx > 50 && (
                                  <div className="text-[9px] opacity-75">
                                    {durationMinutes} min
                                  </div>
                                )}
                              </div>
                              
                              {/* Tooltip */}
                              <div className="absolute left-full ml-2 top-0 hidden group-hover:block z-[100] w-56 bg-white border-2 border-[#C9A961] rounded-lg shadow-2xl p-3 text-[#2C2C2C] pointer-events-none">
                                <div className="space-y-1.5">
                                  <div>
                                    <div className="text-[10px] font-bold text-[#C9A961] uppercase">Cliente</div>
                                    <div className="text-sm font-semibold">{clientName}</div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] font-bold text-[#C9A961] uppercase">Servicio</div>
                                    <div className="text-xs">{serviceName}</div>
                                    <div className="text-[10px] text-[#6B6B6B]">{durationMinutes} minutos</div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] font-bold text-[#C9A961] uppercase">Hora</div>
                                    <div className="text-xs">
                                      {format(bookingDate, 'HH:mm', { locale: es })} - {format(new Date(bookingDate.getTime() + durationMinutes * 60000), 'HH:mm', { locale: es })}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] font-bold text-[#C9A961] uppercase">Personal</div>
                                    <div className="text-xs">{booking.staff_name || staff.find(s => s.id === booking.staff_id)?.name || 'N/A'}</div>
                                  </div>
                                  <div className="pt-1 border-t border-[#C9A961]/20">
                                    <span className={`text-[10px] font-semibold ${
                                      booking.payment_status === 'paid' ? 'text-[#6B7C59]' : 
                                      booking.payment_status === 'partial' ? 'text-[#C9A961]' : 'text-red-500'
                                    }`}>
                                      {booking.payment_status === 'paid' ? '✓ Pagado' : 
                                       booking.payment_status === 'partial' ? '$ Seña' : '⚠ Sin pago'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
        </Card>
      )}

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center flex-wrap gap-4 text-xs">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded bg-yellow-400" />
          <span className="text-[#6B6B6B]">Reservado</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded bg-orange-500" />
          <span className="text-[#6B6B6B]">Confirmado Seña</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded bg-[#6B7C59]" />
          <span className="text-[#6B6B6B]">Confirmado Completo</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded bg-blue-500" />
          <span className="text-[#6B6B6B]">Confirmado Manual</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded bg-gray-400" />
          <span className="text-[#6B6B6B]">Finalizado No Pago</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded bg-[#4a5c3a]" />
          <span className="text-[#6B6B6B]">Finalizado Pagado</span>
        </div>
      </div>

      {/* Create Booking Modal */}
      {showCreateModal && (
        <BookingModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleBookingCreated}
          onClientCreated={(newClient) => {
            setClients([...clients, newClient]);
          }}
          services={services}
          staff={staff}
          clients={clients}
          rooms={rooms}
          locations={locations}
        />
      )}

      {/* Booking Detail Modal */}
      {showDetailModal && selectedBooking && (
        <BookingDetailModal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          booking={selectedBooking}
          onUpdate={handleBookingUpdated}
          services={services}
          staff={staff}
          clients={clients}
          rooms={rooms}
        />
      )}
    </div>
  );
};

export default Bookings;
